import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard, StatCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Target, Sparkles, PlusCircle, Check, X, Flag, Bot, MessageSquare, Send, Trophy, TrendingUp, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const sentColor = { positive: "text-success", neutral: "text-muted-foreground", negative: "text-destructive" };
const riskColor = { High: "bg-destructive/10 text-destructive border-destructive/20", Medium: "bg-warning/10 text-warning border-warning/30", Low: "bg-muted text-muted-foreground border-border" };
const COLORS = ["#FF3B30", "#FF9F0A", "#52525B", "#0B132B", "#00A67E"];

export default function Performance() {
  const { user } = useAuth();
  const isMgr = user?.role === "admin" || user?.role === "manager";
  const [dash, setDash] = useState({});
  const [okrs, setOkrs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [emps, setEmps] = useState([]);

  const load = useCallback(() => {
    api.get("/performance/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/okrs").then((r) => setOkrs(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isMgr) api.get("/employees?status=Active").then((r) => setEmps(r.data)).catch(() => {}); }, [isMgr]);

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Performance Management" title="Performance" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Target} label="Avg KPI Progress" value={`${dash.avg_progress || 0}%`} accent="text-success" />
        <StatCard icon={TrendingUp} label="My KPIs" value={(dash.my_okrs || []).length} />
        {isMgr && <StatCard icon={MessageSquare} label="Pending Reviews" value={dash.pending_reviews || 0} accent="text-warning" />}
        {isMgr && <StatCard icon={Flag} label="At-Risk Flags" value={(dash.flags || []).length} accent="text-destructive" />}
      </div>

      <Tabs defaultValue="okrs">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="okrs" data-testid="tab-okrs">KPIs</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
          {isMgr && <TabsTrigger value="flags" data-testid="tab-flags">At-Risk</TabsTrigger>}
          {isMgr && <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>}
          <TabsTrigger value="bot" data-testid="tab-bot">Policy Bot</TabsTrigger>
        </TabsList>

        <TabsContent value="okrs" className="mt-4"><OKRsTab okrs={okrs} reload={load} isMgr={isMgr} user={user} /></TabsContent>
        <TabsContent value="reviews" className="mt-4"><ReviewsTab reviews={reviews} emps={emps} reload={load} isMgr={isMgr} user={user} /></TabsContent>
        {isMgr && <TabsContent value="flags" className="mt-4"><FlagsTab dash={dash} emps={emps} reload={load} /></TabsContent>}
        {isMgr && <TabsContent value="insights" className="mt-4"><InsightsTab dash={dash} /></TabsContent>}
        <TabsContent value="bot" className="mt-4"><PolicyBot /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- KPIs ---------------- */
function OKRsTab({ okrs, reload, isMgr, user }) {
  const [open, setOpen] = useState(false);
  const [myEmpId, setMyEmpId] = useState(null);
  useEffect(() => { api.get("/employees/me").then((r) => setMyEmpId(r.data.id)).catch(() => {}); }, []);
  const [drafting, setDrafting] = useState(false);
  const [form, setForm] = useState({ objective: "", target: "" });
  const [checkin, setCheckin] = useState({});
  const [prog, setProg] = useState({});

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/okrs/ai-draft", { count: 1 });
      const s = (data.suggestions || [])[0];
      if (s) { setForm({ objective: s.objective, target: s.target || "" }); toast.success("AI draft ready — review & edit"); }
      else toast.error("AI returned no structured draft");
    } catch (e) { toast.error(apiErr(e)); } finally { setDrafting(false); }
  };
  const create = async () => { try { await api.post("/okrs", { objective: form.objective, target: form.target, progress: 0 }); toast.success("KPI created"); setOpen(false); setForm({ objective: "", target: "" }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const saveProgress = async (okr, val) => { await api.put(`/okrs/${okr.id}`, { progress: parseFloat(val || 0) }); toast.success("Progress updated"); reload(); };
  const doCheckin = async (id) => { if (!checkin[id]) return; await api.post(`/okrs/${id}/checkin`, { text: checkin[id] }); setCheckin({ ...checkin, [id]: "" }); toast.success("Check-in saved & analyzed"); reload(); };
  const review = async (id, decision) => { await api.put(`/okrs/${id}/review`, { decision, comment: decision === "approve" ? "Looks good" : "Please revise" }); reload(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="create-okr-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> New KPI</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create KPI</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <Button variant="outline" size="sm" onClick={aiDraft} disabled={drafting} data-testid="okr-ai-draft"><Sparkles className="h-4 w-4 mr-1.5 text-warning" /> {drafting ? "Drafting…" : "AI Draft Suggestion"}</Button>
              <div><Label>Objective</Label><Textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="mt-1.5" placeholder="A single measurable objective" data-testid="okr-objective" /></div>
              <div><Label>Target / Measure</Label><Input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1.5" placeholder="e.g. Reach 95% CSAT by Q4" data-testid="okr-target" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.objective} data-testid="okr-save">Create KPI</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {okrs.length === 0 && <p className="text-muted-foreground text-center py-12">No KPIs yet. Create one or use AI Draft.</p>}
        {okrs.map((o) => (
          <Card key={o.id} className="p-5 rounded-md border-border" data-testid={`okr-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="overline">{o.period} · {o.employee_name}</span>
                  <span className={cn("px-2 py-0.5 rounded text-xs border", o.status === "Approved" ? "bg-success/10 text-success border-success/20" : o.status === "Rejected" ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-muted text-muted-foreground border-border")}>{o.status}</span>
                </div>
                <h3 className="font-heading font-bold text-lg mt-1">{o.objective}</h3>
                {o.target && <p className="text-sm text-muted-foreground mt-0.5">Target: {o.target}</p>}
              </div>
              <div className="text-right shrink-0"><p className="font-heading font-extrabold text-2xl font-mono-num">{o.progress}%</p></div>
            </div>
            <Progress value={o.progress} className="h-2 mt-3" />
            <div className="flex items-center gap-2 mt-3">
              <Label className="text-xs text-muted-foreground">Update progress</Label>
              <Input type="number" min={0} max={100} defaultValue={o.progress} onChange={(e) => setProg({ ...prog, [o.id]: e.target.value })} className="w-24 h-8" data-testid={`okr-progress-input-${o.id}`} />
              <Button size="sm" variant="outline" onClick={() => saveProgress(o, prog[o.id] ?? o.progress)} data-testid={`okr-progress-save-${o.id}`}>Save</Button>
            </div>
            {(o.checkins || []).length > 0 && (
              <div className="mt-3 border-t border-border pt-3 space-y-1">
                {o.checkins.slice(-3).map((c) => (
                  <p key={c.id} className="text-sm"><span className={cn("font-medium", sentColor[c.sentiment])}>[{c.sentiment}{c.flagged ? " ⚠️" : ""}]</span> <span className="text-muted-foreground">{c.text}</span></p>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Input placeholder="Weekly check-in note…" value={checkin[o.id] || ""} onChange={(e) => setCheckin({ ...checkin, [o.id]: e.target.value })} className="h-9" data-testid={`okr-checkin-${o.id}`} onKeyDown={(e) => e.key === "Enter" && doCheckin(o.id)} />
              <Button size="sm" variant="outline" onClick={() => doCheckin(o.id)} data-testid={`okr-checkin-btn-${o.id}`}><Send className="h-4 w-4" /></Button>
            </div>
            {isMgr && o.employee_id !== myEmpId && o.status === "Active" && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="text-success" onClick={() => review(o.id, "approve")} data-testid={`okr-approve-${o.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => review(o.id, "reject")} data-testid={`okr-reject-${o.id}`}><X className="h-4 w-4 mr-1" /> Request Changes</Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
function ReviewsTab({ reviews, emps, reload, isMgr, user }) {
  const [meta, setMeta] = useState({ competencies: [], rating_defs: {}, review_types: [], promotion: [] });
  const [open, setOpen] = useState(false);
  const [create, setCreate] = useState({ employee_id: "", review_type: "Quarterly" });
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/reviews/meta/competencies").then((r) => setMeta(r.data)); }, []);
  const start = async () => { try { await api.post("/reviews", create); toast.success("Review started"); setOpen(false); reload(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      {isMgr && (
        <div className="flex justify-end mb-4">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="start-review-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Start Review</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Start Performance Review</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Employee</Label>
                  <Select value={create.employee_id} onValueChange={(v) => setCreate({ ...create, employee_id: v })}><SelectTrigger className="mt-1.5" data-testid="review-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-popover max-h-64">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Type</Label>
                  <Select value={create.review_type} onValueChange={(v) => setCreate({ ...create, review_type: v })}><SelectTrigger className="mt-1.5" data-testid="review-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">{meta.review_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button onClick={start} disabled={!create.employee_id} data-testid="review-start">Start</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {reviews.length === 0 && <p className="col-span-full text-muted-foreground text-center py-12">No reviews yet.</p>}
        {reviews.map((r) => (
          <Card key={r.id} className="p-5 rounded-md border-border cursor-pointer hover:-translate-y-1 hover:shadow-md transition-transform" onClick={() => setActive(r)} data-testid={`review-${r.id}`}>
            <p className="overline">{r.period}</p>
            <h3 className="font-heading font-bold mt-1">{r.employee_name}</h3>
            <p className="text-sm text-muted-foreground">{r.review_type} Review</p>
            <span className="inline-block mt-3 px-2 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/20">{r.status}</span>
          </Card>
        ))}
      </div>
      {active && <ReviewDialog review={active} meta={meta} isMgr={isMgr} user={user} onClose={() => setActive(null)} reload={reload} />}
    </div>
  );
}

function ReviewDialog({ review, meta, isMgr, user, onClose, reload }) {
  const [r, setR] = useState(review);
  const [self, setSelf] = useState(review.self || {});
  const [mgr, setMgr] = useState(review.manager || { ratings: {} });
  const [promo, setPromo] = useState(review.promotion_readiness || "Not Ready");
  const [devPlan, setDevPlan] = useState(review.development_plan || "");
  const [busy, setBusy] = useState("");
  const [comment, setComment] = useState("");

  const reloadReview = async () => { const { data } = await api.get(`/reviews/${r.id}`); setR(data); };
  const draftSelf = async () => { setBusy("self"); try { const { data } = await api.post(`/reviews/${r.id}/ai-draft-self`); setSelf({ ...self, ...data }); toast.success("AI draft ready"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  const saveSelf = async () => { await api.put(`/reviews/${r.id}/self`, self); toast.success("Self-assessment saved"); reload(); reloadReview(); };
  const aiMgr = async () => { setBusy("mgr"); try { const { data } = await api.post(`/reviews/${r.id}/ai-manager-comments`); setMgr({ ...mgr, strengths: data.strengths, development: data.development, overall_comment: data.overall_comment }); toast.success("AI comments drafted"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  const saveMgr = async () => { await api.put(`/reviews/${r.id}/manager`, { manager: mgr, promotion_readiness: promo, development_plan: devPlan }); toast.success("Manager assessment saved"); reload(); reloadReview(); };
  const summary = async () => { setBusy("sum"); try { const { data } = await api.post(`/reviews/${r.id}/ai-summary`); setR({ ...r, executive_summary: data.executive_summary }); toast.success("Executive summary generated"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  const finalize = async () => { await api.put(`/reviews/${r.id}/status`, { status: "Finalized" }); toast.success("Review finalized"); reload(); reloadReview(); };
  const addComment = async () => { if (!comment) return; await api.post(`/reviews/${r.id}/comment`, { text: comment }); setComment(""); reloadReview(); };

  const SELF_FIELDS = [["achievements", "Achievements"], ["strengths", "Strengths"], ["improvements", "Areas for Improvement"], ["challenges", "Challenges Faced"], ["career_goals", "Career Goals"]];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{r.employee_name} · {r.review_type} · {r.period}</DialogTitle></DialogHeader>
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/20 w-fit">{r.status}</span>

        {r.executive_summary && <div className="p-3 rounded-md bg-muted/60 border border-border text-sm"><p className="overline mb-1">AI Executive Summary</p>{r.executive_summary}</div>}

        {/* Self-assessment */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2"><h4 className="font-heading font-bold">Self-Assessment</h4>
            <Button size="sm" variant="outline" onClick={draftSelf} disabled={busy === "self"} data-testid="ai-draft-self"><Sparkles className="h-4 w-4 mr-1 text-warning" /> {busy === "self" ? "…" : "AI Draft"}</Button></div>
          <div className="space-y-2">
            {SELF_FIELDS.map(([k, label]) => (
              <div key={k}><Label className="text-xs">{label}</Label><Textarea rows={2} value={self[k] || ""} onChange={(e) => setSelf({ ...self, [k]: e.target.value })} className="mt-1" data-testid={`self-${k}`} /></div>
            ))}
          </div>
          <Button size="sm" className="mt-2" onClick={saveSelf} data-testid="save-self">Save Self-Assessment</Button>
        </div>

        {/* Manager assessment */}
        {isMgr && (
          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2"><h4 className="font-heading font-bold">Manager Assessment</h4>
              <Button size="sm" variant="outline" onClick={aiMgr} disabled={busy === "mgr"} data-testid="ai-mgr-comments"><Sparkles className="h-4 w-4 mr-1 text-warning" /> {busy === "mgr" ? "…" : "AI Comments"}</Button></div>
            <div className="grid grid-cols-2 gap-2">
              {meta.competencies.map((c) => (
                <div key={c}><Label className="text-xs">{c}</Label>
                  <Select value={String(mgr.ratings?.[c] || "")} onValueChange={(v) => setMgr({ ...mgr, ratings: { ...mgr.ratings, [c]: parseInt(v) } })}>
                    <SelectTrigger className="mt-1 h-9" data-testid={`rating-${c}`}><SelectValue placeholder="Rate 1-5" /></SelectTrigger>
                    <SelectContent className="bg-popover">{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} · {meta.rating_defs[n]}</SelectItem>)}</SelectContent></Select></div>
              ))}
              <div><Label className="text-xs">Overall Rating</Label>
                <Select value={String(mgr.overall || "")} onValueChange={(v) => setMgr({ ...mgr, overall: parseInt(v) })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="rating-overall"><SelectValue placeholder="1-5" /></SelectTrigger>
                  <SelectContent className="bg-popover">{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} · {meta.rating_defs[n]}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Promotion Readiness</Label>
                <Select value={promo} onValueChange={setPromo}><SelectTrigger className="mt-1 h-9" data-testid="promo-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{meta.promotion.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="mt-2 space-y-2">
              <div><Label className="text-xs">Strengths Observed</Label><Textarea rows={2} value={mgr.strengths || ""} onChange={(e) => setMgr({ ...mgr, strengths: e.target.value })} className="mt-1" data-testid="mgr-strengths" /></div>
              <div><Label className="text-xs">Areas for Development</Label><Textarea rows={2} value={mgr.development || ""} onChange={(e) => setMgr({ ...mgr, development: e.target.value })} className="mt-1" data-testid="mgr-development" /></div>
              <div><Label className="text-xs">Development Plan</Label><Textarea rows={2} value={devPlan} onChange={(e) => setDevPlan(e.target.value)} className="mt-1" data-testid="dev-plan" /></div>
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" onClick={saveMgr} data-testid="save-mgr">Save Assessment</Button>
              <Button size="sm" variant="outline" onClick={summary} disabled={busy === "sum"} data-testid="ai-summary"><Sparkles className="h-4 w-4 mr-1 text-warning" /> {busy === "sum" ? "…" : "AI Summary"}</Button>
              <Button size="sm" variant="outline" className="text-success" onClick={finalize} data-testid="finalize-review"><Check className="h-4 w-4 mr-1" /> Finalize</Button>
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="border-t border-border pt-3">
          <h4 className="font-heading font-bold mb-2">Discussion</h4>
          <div className="space-y-1 mb-2">{(r.comments || []).map((c) => <p key={c.id} className="text-sm"><span className="font-medium">{c.name}:</span> <span className="text-muted-foreground">{c.text}</span></p>)}</div>
          <div className="flex gap-2"><Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" className="h-9" data-testid="review-comment" onKeyDown={(e) => e.key === "Enter" && addComment()} /><Button size="icon" variant="outline" className="h-9 w-9" onClick={addComment}><Send className="h-4 w-4" /></Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Flags ---------------- */
function FlagsTab({ dash, emps, reload }) {
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ employee_id: "", reason: "", risk_level: "Medium" });
  const flags = dash.flags || [];

  const aiSuggest = async () => { const { data } = await api.post("/flags/ai-suggest"); setSuggestions(data.suggestions); if (!data.suggestions.length) toast.info("No at-risk patterns detected from check-ins"); };
  const create = async () => { try { await api.post("/flags", form); toast.success("Employee flagged"); setForm({ employee_id: "", reason: "", risk_level: "Medium" }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { await api.delete(`/flags/${id}`); reload(); };
  const accept = async (s) => { await api.post("/flags", { employee_id: s.employee_id, reason: s.reason, risk_level: s.risk_level }); toast.success("Flag added"); setSuggestions(suggestions.filter((x) => x.employee_id !== s.employee_id)); reload(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <SectionCard title="Flag an Employee">
        <div className="space-y-3">
          <div><Label>Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}><SelectTrigger className="mt-1.5" data-testid="flag-emp"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-popover max-h-64">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Risk Level</Label>
            <Select value={form.risk_level} onValueChange={(v) => setForm({ ...form, risk_level: v })}><SelectTrigger className="mt-1.5" data-testid="flag-risk"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">{["Low", "Medium", "High"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" data-testid="flag-reason" /></div>
          <Button onClick={create} disabled={!form.employee_id || !form.reason} data-testid="flag-submit"><Flag className="h-4 w-4 mr-1.5" /> Flag Employee</Button>
        </div>
        <div className="mt-5 border-t border-border pt-4">
          <Button variant="outline" size="sm" onClick={aiSuggest} data-testid="flag-ai-suggest"><Sparkles className="h-4 w-4 mr-1.5 text-warning" /> AI: Suggest At-Risk</Button>
          {suggestions.map((s) => (
            <div key={s.employee_id} className="flex items-center gap-2 mt-2 p-2 rounded-md border border-border text-sm">
              <span className={cn("px-1.5 py-0.5 rounded text-xs border", riskColor[s.risk_level])}>{s.risk_level}</span>
              <div className="flex-1 min-w-0"><p className="font-medium">{s.employee_name}</p><p className="text-xs text-muted-foreground truncate">{s.reason}</p></div>
              <Button size="sm" variant="ghost" onClick={() => accept(s)} data-testid={`accept-flag-${s.employee_id}`}><Check className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </SectionCard>
      <SectionCard title={`At-Risk Watchlist (${flags.length})`}>
        <div className="space-y-2">
          {flags.length === 0 && <p className="text-sm text-muted-foreground">No flagged employees.</p>}
          {flags.map((f) => (
            <div key={f.id} className="flex items-center gap-2 p-3 rounded-md border border-border text-sm" data-testid={`flag-${f.id}`}>
              <span className={cn("px-1.5 py-0.5 rounded text-xs border", riskColor[f.risk_level])}>{f.risk_level}</span>
              <div className="flex-1 min-w-0"><p className="font-medium">{f.employee_name}</p><p className="text-xs text-muted-foreground truncate">{f.reason} · by {f.flagged_by}</p></div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(f.id)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

/* ---------------- Insights ---------------- */
function InsightsTab({ dash }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <SectionCard title="Performance Rating Distribution">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dash.rating_distribution || []}>
            <XAxis dataKey="rating" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "hsl(var(--muted))" }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>{(dash.rating_distribution || []).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>
      <SectionCard title="Promotion-Ready Talent">
        <div className="space-y-2">
          {(dash.promotion_ready || []).length === 0 && <p className="text-sm text-muted-foreground">No promotion-ready employees yet.</p>}
          {(dash.promotion_ready || []).map((p, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-md hover:bg-muted/50">
              <Trophy className="h-4 w-4 text-warning" />
              <span className="flex-1 text-sm font-medium">{p.employee_name}</span>
              <span className="text-xs text-success">{p.promotion_readiness}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

/* ---------------- Policy Bot ---------------- */
function PolicyBot() {
  const [q, setQ] = useState("");
  const [chat, setChat] = useState([]);
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!q.trim()) return;
    const question = q; setQ(""); setChat((c) => [...c, { role: "user", text: question }]); setBusy(true);
    try { const { data } = await api.post("/ai/policy-qa", { question }); setChat((c) => [...c, { role: "bot", text: data.answer }]); }
    catch (e) { setChat((c) => [...c, { role: "bot", text: apiErr(e) }]); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="HR Policy Assistant">
      <p className="text-sm text-muted-foreground mb-4">Ask about leave, benefits or company policy. Answers are AI-generated from uploaded HR policy documents — verify with HR for anything critical.</p>
      <div className="space-y-3 mb-4 max-h-[45vh] overflow-y-auto">
        {chat.length === 0 && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Bot className="h-5 w-5" /> Try: "How many days of annual leave do I get?"</div>}
        {chat.map((m, i) => (
          <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={cn("max-w-[80%] px-3 py-2 rounded-md text-sm", m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div>}
      </div>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a policy question…" data-testid="policy-input" onKeyDown={(e) => e.key === "Enter" && ask()} />
        <Button onClick={ask} disabled={busy} data-testid="policy-ask"><Send className="h-4 w-4" /></Button>
      </div>
    </SectionCard>
  );
}
